import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Auth = ({ setUser }) => {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    company_name: '',
    company_registration: '',
    contact_person: '',
    contact_phone: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      toast.success('Logged in successfully!');
      navigate(user.role === 'admin' ? '/admin' : '/account');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      toast.success('Registered successfully!');
      navigate('/account');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-16 px-6 flex items-center justify-center min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-block px-6 py-3 border-2 border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 mb-4 shadow-[0_0_30px_rgba(212,175,55,0.3)]">
            <p className="text-xs text-white tracking-[0.3em] mb-1">DIAMOND ACCESS</p>
            <h1 className="text-3xl font-black text-[#D4AF37]" data-testid="auth-title">MEMBER PORTAL</h1>
          </div>
          <p className="text-white/70">Exclusive benefits for our valued customers</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 bg-white/5 border border-[#D4AF37]/30">
            <TabsTrigger 
              value="login" 
              data-testid="login-tab" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#D4AF37]/30 data-[state=active]:to-[#D4AF37]/10 data-[state=active]:text-[#D4AF37] data-[state=active]:border data-[state=active]:border-[#D4AF37]"
            >
              Login
            </TabsTrigger>
            <TabsTrigger 
              value="register" 
              data-testid="register-tab" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#D4AF37]/30 data-[state=active]:to-[#D4AF37]/10 data-[state=active]:text-[#D4AF37] data-[state=active]:border data-[state=active]:border-[#D4AF37]"
            >
              Register
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
              <div>
                <Label htmlFor="login-email" className="text-white mb-2 block">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                  className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                />
              </div>

              <div>
                <Label htmlFor="login-password" className="text-white mb-2 block">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                  className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full border-2 border-[#D4AF37] bg-black hover:bg-black hover:shadow-[0_0_40px_rgba(212,175,55,0.8),0_0_60px_rgba(212,175,55,0.4)] text-[#D4AF37] hover:text-white uppercase tracking-widest font-bold py-6 transition-all duration-300"
              >
                {loading ? 'Logging in...' : 'Login to Diamond Access'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-6" data-testid="register-form">
              <div>
                <Label htmlFor="register-company-name" className="text-white mb-2 block">Company Name</Label>
                <Input
                  id="register-company-name"
                  type="text"
                  value={registerData.company_name}
                  onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                  required
                  data-testid="register-company-name-input"
                  className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                />
              </div>

              <div>
                <Label htmlFor="register-company-reg" className="text-white mb-2 block">Company Registration Number</Label>
                <Input
                  id="register-company-reg"
                  type="text"
                  value={registerData.company_registration}
                  onChange={(e) => setRegisterData({ ...registerData, company_registration: e.target.value })}
                  required
                  data-testid="register-company-reg-input"
                  className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                />
              </div>

              <div>
                <Label htmlFor="register-contact-person" className="text-white mb-2 block">Contact Person</Label>
                <Input
                  id="register-contact-person"
                  type="text"
                  value={registerData.contact_person}
                  onChange={(e) => setRegisterData({ ...registerData, contact_person: e.target.value })}
                  required
                  data-testid="register-contact-person-input"
                  className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                />
              </div>

              <div>
                <Label htmlFor="register-phone" className="text-white mb-2 block">Contact Phone Number</Label>
                <Input
                  id="register-phone"
                  type="tel"
                  value={registerData.contact_phone}
                  onChange={(e) => setRegisterData({ ...registerData, contact_phone: e.target.value })}
                  required
                  data-testid="register-phone-input"
                  className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                />
              </div>

              <div>
                <Label htmlFor="register-email" className="text-white mb-2 block">Contact Email Address</Label>
                <Input
                  id="register-email"
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  required
                  data-testid="register-email-input"
                  className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                />
              </div>

              <div>
                <Label htmlFor="register-password" className="text-white mb-2 block">Password</Label>
                <Input
                  id="register-password"
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  required
                  data-testid="register-password-input"
                  className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="register-submit-button"
                className="w-full border-2 border-[#D4AF37] bg-black hover:bg-black hover:shadow-[0_0_40px_rgba(212,175,55,0.8),0_0_60px_rgba(212,175,55,0.4)] text-[#D4AF37] hover:text-white uppercase tracking-widest font-bold py-6 transition-all duration-300"
              >
                {loading ? 'Registering...' : 'Register for Diamond Access'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
};

export default Auth;